import { useEffect, useMemo, useRef, useState } from "react";

import stylesFindFrame from "../../styles/components/FindFrame.module.css";
import styles from "../../styles/components/MailAccountsToolbar.module.css";

import { ReactComponent as SearchIcon } from "../../assets/icons/glass.svg";
import { ReactComponent as DownIcon } from "../../assets/icons/down.svg";
import { ReactComponent as ResetIcon } from "../../assets/icons/reset.svg";

import SearchInput from "../../ui/inputs/SearchInput";
import Avatar from "../../ui/avatar/Avatar";
import { sortAccountsByName } from "../../utils/accountOptions";
import {
  EMPTY_MAIL_ACCOUNT_FILTERS,
  NO_RESPONSIBLE_FILTER,
  buildResponsibleOptions,
  filterMailAccounts,
  mailAccountTitle,
} from "./mailAccountAdminHelpers";

const MAX_SUGGESTIONS = 3;
const NO_RESPONSIBLE_LABEL = "Sin responsable";
const ALL_ACCOUNTS_LABEL = "Todas las cuentas";
const ALL_RESPONSIBLES_LABEL = "Todos los responsables";

function optionKeyDown(select) {
  return (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      select();
    }
  };
}

function FrameSelect({ title, selectedLabel, selectedAvatar, options, allLabel, isOpen, onToggle, onSelect }) {
  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onToggle();
    }
  };

  return (
    <div className={stylesFindFrame.inputdropdownUser}>
      <div className={stylesFindFrame.byVisibility}>{title}</div>

      <div
        className={stylesFindFrame.userParent}
        role="button"
        tabIndex={0}
        aria-label={title}
        aria-expanded={isOpen}
        onClick={onToggle}
        onKeyDown={handleKeyDown}
        style={{ cursor: "pointer" }}
      >
        <div className={stylesFindFrame.user}>
          {selectedAvatar && <Avatar src={selectedAvatar.src} name={selectedAvatar.name} size={24} />}
          <div className={stylesFindFrame.mailaccount}>{selectedLabel}</div>
        </div>

        <div className={stylesFindFrame.dropdownArrowWrapper}>
          <DownIcon className={stylesFindFrame.dropdownArrow} />
        </div>
      </div>

      {isOpen && (
        <div className={stylesFindFrame.dropdownMenu} role="listbox" aria-label={`Opciones de ${title.toLowerCase()}`}>
          <div
            className={stylesFindFrame.dropdownItem}
            role="option"
            tabIndex={0}
            aria-label={allLabel}
            aria-selected={false}
            onClick={() => onSelect("")}
            onKeyDown={optionKeyDown(() => onSelect(""))}
          >
            {allLabel}
          </div>

          {options.map((option) => (
            <div
              key={option.id}
              className={stylesFindFrame.dropdownItem}
              role="option"
              tabIndex={0}
              aria-label={option.label}
              aria-selected={false}
              onClick={() => onSelect(String(option.id))}
              onKeyDown={optionKeyDown(() => onSelect(String(option.id)))}
            >
              {option.avatar && <Avatar src={option.avatar.src} name={option.avatar.name} size={24} />}
              <span>{option.label}</span>
              {option.hint && <span className={styles.optionEmail}>{option.hint}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FilterChip({ label, value, onRemove, removeLabel }) {
  return (
    <span className={styles.chip}>
      <span className={styles.chipLabel}>{label}</span>
      {value}
      <button type="button" className={styles.chipRemove} onClick={onRemove} aria-label={removeLabel}>
        ×
      </button>
    </span>
  );
}

function responsibleLabelFor(responsibleId, responsibleOptions, fallbackLabel) {
  if (!responsibleId) return fallbackLabel;
  if (responsibleId === NO_RESPONSIBLE_FILTER) return NO_RESPONSIBLE_LABEL;
  const responsible = responsibleOptions.find((option) => String(option.id) === String(responsibleId));
  return responsible?.name ?? fallbackLabel;
}

export function MailAccountFilterChips({ mailAccounts = [], accounts = [], filters = EMPTY_MAIL_ACCOUNT_FILTERS, onFilterChange }) {
  const responsibleOptions = useMemo(() => buildResponsibleOptions(mailAccounts), [mailAccounts]);
  const appliedAccount = accounts.find((account) => String(account.id) === String(filters.accountId));
  const appliedResponsibleLabel = filters.responsibleId
    ? responsibleLabelFor(filters.responsibleId, responsibleOptions, ALL_RESPONSIBLES_LABEL)
    : null;

  if (!appliedAccount && !appliedResponsibleLabel) return null;

  return (
    <div className={styles.chips}>
      {appliedAccount && (
        <FilterChip
          label="Cuenta:"
          value={appliedAccount.name ?? "-"}
          removeLabel="Quitar filtro de cuenta"
          onRemove={() => onFilterChange?.({ ...filters, accountId: "" })}
        />
      )}
      {appliedResponsibleLabel && (
        <FilterChip
          label={filters.responsibleId === NO_RESPONSIBLE_FILTER ? "" : "Responsable:"}
          value={appliedResponsibleLabel}
          removeLabel="Quitar filtro de responsable"
          onRemove={() => onFilterChange?.({ ...filters, responsibleId: "" })}
        />
      )}
    </div>
  );
}

const MailAccountsToolbar = ({
  mailAccounts = [],
  accounts = [],
  filters = EMPTY_MAIL_ACCOUNT_FILTERS,
  onFilterChange,
  onOpenMailAccount,
  searchWidth = 241,
}) => {
  const sortedAccounts = useMemo(() => sortAccountsByName(accounts), [accounts]);
  const responsibleOptions = useMemo(() => buildResponsibleOptions(mailAccounts), [mailAccounts]);

  const [inputValue, setInputValue] = useState(filters.query ?? "");
  const [draftAccountId, setDraftAccountId] = useState(filters.accountId ?? "");
  const [draftResponsibleId, setDraftResponsibleId] = useState(filters.responsibleId ?? "");
  const [isOpen, setIsOpen] = useState(false);
  const [openSelect, setOpenSelect] = useState(null);

  const searchAreaRef = useRef(null);

  useEffect(() => {
    setInputValue(filters.query ?? "");
    setDraftAccountId(filters.accountId ?? "");
    setDraftResponsibleId(filters.responsibleId ?? "");
  }, [filters.query, filters.accountId, filters.responsibleId]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event) => {
      if (searchAreaRef.current && !searchAreaRef.current.contains(event.target)) {
        setIsOpen(false);
        setOpenSelect(null);
        setInputValue(filters.query ?? "");
        setDraftAccountId(filters.accountId ?? "");
        setDraftResponsibleId(filters.responsibleId ?? "");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, filters.query, filters.accountId, filters.responsibleId]);

  const suggestions = useMemo(() => {
    const query = inputValue.trim();
    if (!query) return [];
    return filterMailAccounts(mailAccounts, { ...EMPTY_MAIL_ACCOUNT_FILTERS, query }).slice(0, MAX_SUGGESTIONS);
  }, [mailAccounts, inputValue]);

  const closeFrame = () => {
    setIsOpen(false);
    setOpenSelect(null);
  };

  const dismissFrame = () => {
    setInputValue(filters.query ?? "");
    setDraftAccountId(filters.accountId ?? "");
    setDraftResponsibleId(filters.responsibleId ?? "");
    closeFrame();
  };

  const applyFilters = (nextFilters) => {
    onFilterChange?.(nextFilters);
    closeFrame();
  };

  const handleSearchSubmit = () => {
    applyFilters({ query: inputValue.trim(), accountId: draftAccountId, responsibleId: draftResponsibleId });
  };

  const handleReset = () => {
    setInputValue("");
    setDraftAccountId("");
    setDraftResponsibleId("");
    applyFilters(EMPTY_MAIL_ACCOUNT_FILTERS);
  };

  const handleSuggestionClick = (mailAccount) => {
    onOpenMailAccount?.(mailAccount);
    dismissFrame();
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSearchSubmit();
    }
    if (event.key === "Escape") {
      dismissFrame();
    }
  };

  const toggleSelect = (name) => setOpenSelect((current) => (current === name ? null : name));

  const draftAccount = sortedAccounts.find((account) => String(account.id) === String(draftAccountId));
  const draftResponsible = responsibleOptions.find((responsible) => String(responsible.id) === String(draftResponsibleId));

  const accountOptions = sortedAccounts.map((account) => ({
    id: account.id,
    label: account.name ?? "-",
    avatar: { src: account.photo ?? null, name: account.name ?? "-" },
  }));

  const responsibleSelectOptions = [
    { id: NO_RESPONSIBLE_FILTER, label: NO_RESPONSIBLE_LABEL },
    ...responsibleOptions.map((responsible) => ({
      id: responsible.id,
      label: responsible.name,
      hint: responsible.email,
      avatar: { src: null, name: responsible.name },
    })),
  ];

  const renderSuggestions = () => {
    if (!inputValue.trim()) return null;
    return (
      <div className={stylesFindFrame.tabSearchParent}>
        {suggestions.length === 0 && (
          <div className={stylesFindFrame.tabSearch}>
            <div className={stylesFindFrame.novaSmartphones}>Ninguna casilla coincide con el texto</div>
          </div>
        )}
        {suggestions.map((mailAccount) => (
          <div
            key={mailAccount.id}
            className={stylesFindFrame.tabSearch}
            role="button"
            tabIndex={0}
            style={{ cursor: "pointer" }}
            onClick={() => handleSuggestionClick(mailAccount)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handleSuggestionClick(mailAccount);
              }
            }}
          >
            <div className={stylesFindFrame.iconsglassParent}>
              <div className={stylesFindFrame.iconsglass}>
                <SearchIcon className={stylesFindFrame.vectorIcon} />
              </div>
              <div className={stylesFindFrame.novaSmartphones}>
                <span>{mailAccountTitle(mailAccount)}</span>
                <span className={stylesFindFrame.rtphones}>
                  {mailAccount.email} · {mailAccount.account_name ?? "-"}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderFrame = () => (
    <div className={`${stylesFindFrame.frameParent} ${styles.frame}`}>
      {renderSuggestions()}

      <div className={stylesFindFrame.filters}>
        <FrameSelect
          title="Por cuenta"
          selectedLabel={draftAccount?.name ?? ALL_ACCOUNTS_LABEL}
          selectedAvatar={draftAccount ? { src: draftAccount.photo ?? null, name: draftAccount.name ?? "-" } : null}
          options={accountOptions}
          allLabel={ALL_ACCOUNTS_LABEL}
          isOpen={openSelect === "account"}
          onToggle={() => toggleSelect("account")}
          onSelect={(accountId) => {
            setDraftAccountId(accountId);
            setOpenSelect(null);
          }}
        />

        <FrameSelect
          title="Por responsable"
          selectedLabel={responsibleLabelFor(draftResponsibleId, responsibleOptions, ALL_RESPONSIBLES_LABEL)}
          selectedAvatar={draftResponsible ? { src: null, name: draftResponsible.name } : null}
          options={responsibleSelectOptions}
          allLabel={ALL_RESPONSIBLES_LABEL}
          isOpen={openSelect === "responsible"}
          onToggle={() => toggleSelect("responsible")}
          onSelect={(responsibleId) => {
            setDraftResponsibleId(responsibleId);
            setOpenSelect(null);
          }}
        />

        <div className={stylesFindFrame.buttonParent}>
          <div
            className={stylesFindFrame.button}
            role="button"
            tabIndex={0}
            onClick={handleSearchSubmit}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handleSearchSubmit();
              }
            }}
            style={{ cursor: "pointer" }}
          >
            <div className={stylesFindFrame.action}>Buscar</div>
          </div>

          <div
            className={stylesFindFrame.iconsresetWrapper}
            role="button"
            tabIndex={0}
            aria-label="Limpiar filtros"
            onClick={handleReset}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handleReset();
              }
            }}
            style={{ cursor: "pointer" }}
          >
            <div className={stylesFindFrame.iconsglass}>
              <ResetIcon className={stylesFindFrame.vectorIcon} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div ref={searchAreaRef} className={styles.searchArea} style={{ width: searchWidth }}>
      <div
        className={styles.searchInputWrapper}
        onClick={() => setIsOpen(true)}
        onFocus={() => setIsOpen(true)}
        onKeyDown={handleSearchKeyDown}
      >
        <SearchInput
          value={inputValue}
          onChange={setInputValue}
          placeholder="Buscar casillas..."
          width="100%"
          height={40}
          minChars={0}
          debounceMs={0}
        />
      </div>

      {isOpen && renderFrame()}
    </div>
  );
};

export default MailAccountsToolbar;
